import { Card } from '../../../shared/components/ui/Card';
import { Box, useTheme } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { roomService } from '../../../shared/services/rooms/roomsServices';
import { ModalDelete } from '../components/ModalDelete';
import { useState } from 'react';
import { RoomActionButtons } from '../components/RoomDetail/RoomActionButtons';
import { RoomMainInfo } from '../components/RoomDetail/RoomMainInfo';
import { RoomSchedules } from '../components/RoomDetail/RoomSchedules';
import { RoomStats } from '../components/RoomDetail/RoomStats';
import { RoomHeader } from '../components/RoomDetail/RoomHeader';



interface Schedule {
  id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  interval_minutes: number;
  is_active: boolean;
}

interface Room {
  id: string;
  name: string;
  description?: string;
  tenant_id: string;
  tenant_name?: string;
  status?: string;
  capacity?: number;
  location?: string;
  schedules?: Schedule[];
  createdAt?: string;
  updatedAt?: string;
}

const STATUS_TRANSLATIONS: Record<string, { label: string; color: any }> = {
  ACTIVE: { label: 'Ativo', color: 'success' },
  INACTIVE: { label: 'Inativo', color: 'default' },
  MAINTENANCE: { label: 'Manutenção', color: 'warning' },
};

export default function RoomDetailPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [modalDelete, setModalDelete] = useState(false);

  const { data, isLoading } = useQuery<any>({
    queryKey: ['room', id],
    queryFn: () => roomService.getById(id!).then(res => res.data),
    enabled: !!id
  });

  const deleteMutation = useMutation({
    mutationFn: () => roomService.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rooms'] });
      navigate('/rooms');
    }
  });

  const handleConfirmDelete = () => {
    deleteMutation.mutate();
  };

  if (isLoading) return null;
  if (!data) return null;

  const statusInfo = STATUS_TRANSLATIONS[data.status || 'ACTIVE'] || { label: data.status || 'Ativo', color: 'default' };

  return (
    <Box sx={{ p: { xs: 1, md: 4 }, background: '#F6F8FB', minHeight: '100vh' }}>
      <Card sx={{ background: '#fff', borderRadius: 4, p: 4, maxWidth: 900, margin: '32px auto', boxShadow: '0 2px 8px 0 rgba(16,30,54,0.04)' }}>
        <RoomHeader name={data.name} />
        <RoomActionButtons 
          onEdit={() => navigate(`/rooms/${data.id}/edit`)} 
          onDelete={() => setModalDelete(true)} 
        />
        <RoomMainInfo 
          name={data.name}
          status={data.status || 'ACTIVE'}
          statusLabel={statusInfo.label}
          statusColor={statusInfo.color}
          tenantName={data.tenant_name}
          createdAt={data.createdAt}
          id={data.id}
          virtualKey={data.virtualKey}
        />
        {/* Grid de informações pode ser separado se desejar, mas mantido inline aqui */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
          <Box sx={{ background: '#F6F7FB', borderRadius: 2, p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <span>
              <strong>Nome:</strong> {data.name}
            </span>
            <span>
              <strong>Status:</strong> {statusInfo.label}
            </span>
          </Box>
          <Box sx={{ background: '#F6F7FB', borderRadius: 2, p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <span>
              <strong>Empresa:</strong> {data.tenant_name}
            </span>
            <span>
              <strong>Criado em:</strong> {data.createdAt ? new Date(data.createdAt).toLocaleDateString('pt-BR') : '-'}
            </span>
          </Box>
        </Box>
        <RoomSchedules schedules={data.schedules || []} />
        <RoomStats schedules={data.schedules || []} />
        <ModalDelete
          open={modalDelete}
          onClose={() => setModalDelete(false)}
          onConfirm={handleConfirmDelete}
          loading={deleteMutation.isPending}
          roomName={data.name}
        />
      </Card>
    </Box>
  );
}